import { InfiniteData, useQueryClient } from '@tanstack/react-query'
import { DailyRecordKind } from 'modules/pond/api'
import {
  useMutatePutDashboardMedicine,
  useMutatePutDashboardFoodNote,
  useMutatePatchDashboardRecord,
} from 'modules/pond/api/mutation'
import { DashboardResponse } from 'modules/pond/api/types'
import { useSnackbar } from 'notistack'
import { useState, useCallback, useRef } from 'react'

import { LabelGroup, LabelName } from '../constants'
import { generateCellKey } from '../utils'
import { ProcessedRow } from './useProcessedRows'

interface CellChangeParams {
  group: string
  label: string
  id: number
  columnIndex: number
  value: string
}

// A cell value as it went to the server, so the cached table can be patched.
interface SentCell {
  group: string
  label: string
  value: string | number | null
}

type DashboardTablePages = InfiniteData<{ data: DashboardResponse; nextPage: number | null }>

const POND_DASHBOARD_TABLE_KEY = 'pond-dashboard-table'

const SHRIMP_HEALTH_FIELDS: Record<string, string> = {
  [LabelName.ShrimpLiverYellow]: 'liver_yellow',
  [LabelName.ShrimpLiverGreen]: 'liver_green',
  [LabelName.ShrimpLiverBlack]: 'liver_black',
  [LabelName.ShrimpLiverGreenYellow]: 'liver_green_yellow',
  [LabelName.ShrimpStomachYellow]: 'stomach_yellow',
  [LabelName.ShrimpStomachGreen]: 'stomach_green',
  [LabelName.ShrimpStomachBlack]: 'stomach_black',
  [LabelName.ShrimpStomachGreenYellow]: 'stomach_green_yellow',
}

const WATER_VIBRIO_FIELDS: Record<string, string> = {
  [LabelName.WaterGreen]: 'green_vibrio',
  [LabelName.WaterYellow]: 'yellow_vibrio',
  [LabelName.WaterBlack]: 'black_vibrio',
  [LabelName.WaterGreenYellow]: 'green_yellow_vibrio',
}

const WATER_TREATMENT_FIELDS: Record<string, string> = {
  [LabelName.WaterTurbidity]: 'turbidity',
  [LabelName.WaterLevel]: 'water_level',
  [LabelName.WaterRefill]: 'water_addition',
  [LabelName.WaterDrain]: 'water_transfer',
  [LabelName.WaterFlow]: 'water_flow',
  [LabelName.WaterColor]: 'water_color_id',
  [LabelName.WaterLime]: 'lime_white',
  [LabelName.WaterCement]: 'lime_cement',
  [LabelName.WaterDye]: 'water_dye',
  [LabelName.WaterMolasses]: 'molasses',
  [LabelName.WaterFlocculant]: 'flocculant',
  [LabelName.WaterAmmonia]: 'ammonia',
  [LabelName.WaterNitrite]: 'nitrite',
  [LabelName.WaterAlkaline]: 'alkali',
  [LabelName.WaterMicrobe]: 'microorganism',
  [LabelName.WaterPHMorning]: 'ph_morning',
  [LabelName.WaterPHEvening]: 'ph_evening',
  [LabelName.WaterDOMax]: 'do_max',
  [LabelName.WaterDOMin]: 'do_min',
  [LabelName.WaterTempMorning]: 'temperature_morning',
  [LabelName.WaterTempEvening]: 'temperature_evening',
}

// The cached table stores "-" for an empty cell, like the API does.
const toCachedValue = (value: SentCell['value']) =>
  value === null || value === undefined || (typeof value === 'number' && Number.isNaN(value))
    ? '-'
    : value

// Write the values just saved into every cached page of the daily table, so a
// remounted table (back from another tab) shows them at once instead of the
// pre-edit values until its refetch lands.
const patchCachedTable = (
  pages: DashboardTablePages | undefined,
  id: number,
  sent: SentCell[]
): DashboardTablePages | undefined => {
  if (!pages) return pages
  return {
    ...pages,
    pages: pages.pages.map((page) => ({
      ...page,
      data: {
        ...page.data,
        dashboard: {
          ...page.data.dashboard,
          rows: page.data.dashboard.rows.map((row) => {
            const cell = sent.find((s) => s.group === row.group && s.label === row.label)
            const idx = row.id.findIndex((rowId) => Number(rowId) === id)
            if (!cell || idx < 0) return row
            const values = [...row.values]
            values[idx] = toCachedValue(cell.value)
            return { ...row, values }
          }),
        },
      },
    })),
  }
}

const toFeedKg = (value: string | number | undefined) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

// NK-305: write a saved meal into the cached table and move อาหารสะสม by the
// change in kg, for that day and every later day. Pages run newest first and so
// do the columns inside a page, so "later" is every earlier page plus columns
// 0..idx of the meal's own page. This replaces refetching every loaded page
// after each feed edit — tens of seconds and a burst of requests once the user
// has scrolled weeks back — during which อาหารสะสม still showed the old totals.
// The อาหารสะสม patch below assumes one meal cell and one อาหารสะสม cell per date column on
// every page — the backend sends one meal entry per Meal row, so a day with two meals breaks
// that (the selector is already misaligned there). Such a table is refetched instead.
const mealColumnsAligned = (pages: DashboardTablePages) =>
  pages.pages.every((page) => {
    const { columns, rows } = page.data.dashboard
    const lengthOf = (group: string) => rows.find((row) => row.group === group)?.values.length
    return (
      lengthOf(LabelGroup.FoodPerDay) === columns.length &&
      lengthOf(LabelGroup.FoodTotal) === columns.length
    )
  })

const containsMeal = (pages: DashboardTablePages, mealId: number) =>
  pages.pages.some((page) =>
    page.data.dashboard.rows.some(
      (row) =>
        row.group === LabelGroup.FoodPerDay && row.id.some((rowId) => Number(rowId) === mealId)
    )
  )

// Adding kg in the browser leaves float noise (0 + 26.1 - 1.7 = 24.400000000000002).
const roundKg = (kg: number) => Math.round(kg * 1000) / 1000

const patchMealInCache = (
  pages: DashboardTablePages | undefined,
  mealId: number,
  change: { brand?: number | null; amount?: number | null }
): DashboardTablePages | undefined => {
  if (!pages) return pages
  const rowsOf = (page: DashboardTablePages['pages'][number]) => page.data.dashboard.rows
  const pageIdx = pages.pages.findIndex((page) =>
    rowsOf(page).some(
      (row) =>
        row.group === LabelGroup.FoodPerDay && row.id.some((rowId) => Number(rowId) === mealId)
    )
  )
  if (pageIdx < 0) return pages
  const amountRow = rowsOf(pages.pages[pageIdx]).find((row) => row.group === LabelGroup.FoodPerDay)
  const colIdx = amountRow ? amountRow.id.findIndex((rowId) => Number(rowId) === mealId) : -1
  if (!amountRow || colIdx < 0) return pages
  const delta =
    change.amount === undefined
      ? 0
      : toFeedKg(change.amount ?? undefined) - toFeedKg(amountRow.values[colIdx])

  return {
    ...pages,
    pages: pages.pages.map((page, p) => {
      if (p > pageIdx) return page
      return {
        ...page,
        data: {
          ...page.data,
          dashboard: {
            ...page.data.dashboard,
            rows: rowsOf(page).map((row) => {
              if (
                p === pageIdx &&
                row.group === LabelGroup.FoodPerDay &&
                change.amount !== undefined
              ) {
                const values = [...row.values]
                values[colIdx] = toCachedValue(change.amount)
                return { ...row, values }
              }
              if (
                p === pageIdx &&
                row.group === LabelGroup.BrandFood &&
                change.brand !== undefined
              ) {
                const values = [...row.values]
                values[colIdx] = toCachedValue(change.brand)
                return { ...row, values }
              }
              if (row.group === LabelGroup.FoodTotal && delta !== 0) {
                const values = row.values.map((v, i) =>
                  p < pageIdx || i <= colIdx ? roundKg(toFeedKg(v) + delta) : v
                )
                return { ...row, values }
              }
              return row
            }),
          },
        },
      }
    }),
  }
}

export const useCellEditor = (flatRows: ProcessedRow[]) => {
  const [editedCells, setEditedCells] = useState<Map<string, string>>(new Map())
  const editedCellsRef = useRef<Map<string, string>>(new Map())
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  // NK-305: the cells are memoised and keep the first onChange they were
  // rendered with, so the closure below can be one built from an old (cached)
  // copy of the table. Everything read at save time therefore comes from refs.
  // Before this, a value saved, then a tab switch, then an edit to another cell
  // of the same day sent the first value as null and wiped it on the server.
  const rowsRef = useRef(flatRows)
  rowsRef.current = flatRows

  // NK-305: one save at a time per record. Each save sends every field of the
  // record (the server overwrites them all), so two in flight at once could
  // land out of order and the older body would erase the newer value.
  const queuesRef = useRef<Map<string, Promise<unknown>>>(new Map())

  const putDashboardMedicine = useMutatePutDashboardMedicine()
  const putDashboardFoodNote = useMutatePutDashboardFoodNote()
  const patchDashboardRecord = useMutatePatchDashboardRecord()

  // Latest value of one field of the record `id`: the user's edit if any,
  // otherwise the newest table data.
  const getCurrentValue = (
    groupName: string,
    labelName: string,
    id: number,
    fallbackColIdx: number
  ) => {
    const rows = rowsRef.current
    const row = rows.find((r) => r.group === groupName && r.label === labelName)
    if (!row) return undefined
    // Locate the column by the record id within this row (fields of one record
    // share the row's id array; ids repeat across tables, so never search other
    // rows): a column index captured earlier can point elsewhere once more
    // pages have loaded.
    const idxById = row.id.findIndex((rowId) => Number(rowId) === id)
    const colIdx = idxById >= 0 ? idxById : fallbackColIdx
    const cellId = row.id[colIdx]
    const cellKey = generateCellKey(row.group, row.label, cellId)
    const edited = editedCellsRef.current.get(cellKey)
    return edited !== undefined ? edited : row.values[colIdx]
  }

  // NK-305 / ADR-012 phase 1: the multi-field records are saved with a field-level PATCH
  // carrying only the edited field (null clears it). The PUT overwrote every field, so the
  // whole day had to be sent and a stale sibling value could erase a saved one.
  const patchField = async (
    kind: DailyRecordKind,
    id: number,
    field: string,
    value: string | number | null
  ) => {
    await patchDashboardRecord.mutateAsync({ kind, body: { id, [field]: value } })
  }

  // The edited cell's value as the API takes it: "-" / empty → null.
  const numberOrNull = (val: string | number | undefined) => {
    if (val === undefined || val === '' || val === '-') return null
    const n = Number(val)
    return Number.isNaN(n) ? null : n
  }
  // A cleared text cell holds the table's "-" placeholder; send null so the field is
  // cleared instead of storing a literal "-".
  const textOrNull = (val: string | number | undefined) =>
    val === undefined || val === '' || val === '-' ? null : String(val)

  const send = async (params: CellChangeParams): Promise<SentCell[]> => {
    const { id, columnIndex: colIdx } = params
    const edited = getCurrentValue(params.group, params.label, id, colIdx)
    const sentCell = (value: string | number | null): SentCell[] => [
      { group: params.group, label: params.label, value },
    ]

    if (
      [LabelGroup.Drug, LabelGroup.Vitamin, LabelGroup.MicrobeFood].includes(
        params.group as LabelGroup
      )
    ) {
      const medicineNameId = numberOrNull(edited)
      await putDashboardMedicine.mutateAsync({ body: { id, medicine_name_id: medicineNameId } })
      return sentCell(medicineNameId)
    }

    if (params.group === LabelGroup.BrandFood || params.group === LabelGroup.FoodPerDay) {
      const value = numberOrNull(edited)
      const isBrand = params.group === LabelGroup.BrandFood
      await patchField('meal', id, isBrand ? 'food_brand_id' : 'amount', value)
      let refetchTable = queryClient.isFetching({ queryKey: [POND_DASHBOARD_TABLE_KEY] }) > 0
      queryClient.setQueriesData<DashboardTablePages>([POND_DASHBOARD_TABLE_KEY], (pages) => {
        if (!pages || !containsMeal(pages, id)) return pages
        if (!mealColumnsAligned(pages)) {
          refetchTable = true
          return pages
        }
        return patchMealInCache(pages, id, isBrand ? { brand: value } : { amount: value })
      })
      // A load that started before this save would land afterwards with the old อาหารสะสม
      // and overwrite the patch; invalidating cancels it and refetches after the save.
      if (refetchTable) queryClient.invalidateQueries([POND_DASHBOARD_TABLE_KEY])
      return []
    }

    if (params.group === LabelGroup.Note) {
      const note = String(edited ?? '')
      await putDashboardFoodNote.mutateAsync({ body: { id, note } })
      return sentCell(note)
    }

    if (params.group === LabelGroup.NoteOwner || params.group === LabelGroup.NoteWorker) {
      const note = textOrNull(edited)
      await patchField(
        'daily-note',
        id,
        params.group === LabelGroup.NoteOwner ? 'note_owner' : 'note_worker',
        note
      )
      return sentCell(note)
    }

    if (params.group === LabelGroup.ShrimpResult && SHRIMP_HEALTH_FIELDS[params.label]) {
      const value = numberOrNull(edited)
      await patchField('shrimp-health', id, SHRIMP_HEALTH_FIELDS[params.label], value)
      return sentCell(value)
    }

    if (params.group === LabelGroup.WaterResult && WATER_VIBRIO_FIELDS[params.label]) {
      const value = numberOrNull(edited)
      await patchField('water-vibrio', id, WATER_VIBRIO_FIELDS[params.label], value)
      return sentCell(value)
    }

    if (params.group === LabelGroup.WaterResult && WATER_TREATMENT_FIELDS[params.label]) {
      const field = WATER_TREATMENT_FIELDS[params.label]
      const value = field === 'microorganism' ? textOrNull(edited) : numberOrNull(edited)
      await patchField('water-treatment', id, field, value)
      return sentCell(value)
    }

    return []
  }

  // Records are keyed by endpoint as well as id: ids are only unique per table.
  const recordKey = (params: CellChangeParams) => {
    if (
      [LabelGroup.Drug, LabelGroup.Vitamin, LabelGroup.MicrobeFood].includes(
        params.group as LabelGroup
      )
    )
      return `medicine:${params.id}`
    if (params.group === LabelGroup.BrandFood || params.group === LabelGroup.FoodPerDay)
      return `meal:${params.id}`
    if (params.group === LabelGroup.NoteOwner || params.group === LabelGroup.NoteWorker)
      return `daily-note:${params.id}`
    if (params.group === LabelGroup.WaterResult) {
      return WATER_VIBRIO_FIELDS[params.label]
        ? `water-vibrio:${params.id}`
        : `water-treatment:${params.id}`
    }
    return `${params.group}:${params.id}`
  }

  const handleCellChange = useCallback(
    async (params: CellChangeParams) => {
      const key = generateCellKey(params.group, params.label, params.id)

      // Update ref immediately for synchronous access
      const newMap = new Map<string, string>(editedCellsRef.current)
      newMap.set(key, params.value)
      editedCellsRef.current = newMap

      // Update state to trigger re-render
      setEditedCells(newMap)

      const queueKey = recordKey(params)
      const previous = queuesRef.current.get(queueKey) ?? Promise.resolve()
      const task = previous
        .catch(() => undefined)
        .then(() => send(params))
        .then((sent) => {
          if (sent.length > 0) {
            queryClient.setQueriesData<DashboardTablePages>([POND_DASHBOARD_TABLE_KEY], (pages) =>
              patchCachedTable(pages, params.id, sent)
            )
          }
        })
      queuesRef.current.set(queueKey, task)

      try {
        await task
      } catch (error) {
        // Revert ref and state on error — unless the cell has been edited again
        // since, in which case the newer value stays for the next save.
        if (editedCellsRef.current.get(key) === params.value) {
          const revertedMap = new Map<string, string>(editedCellsRef.current)
          revertedMap.delete(key)
          editedCellsRef.current = revertedMap
          setEditedCells(revertedMap)
        }

        enqueueSnackbar('Update failed', { variant: 'error' })
      } finally {
        if (queuesRef.current.get(queueKey) === task) queuesRef.current.delete(queueKey)
      }
    },
    // send/recordKey read only refs and stable mutation handles.
    [enqueueSnackbar, queryClient]
  )

  return { editedCells, handleCellChange }
}
